import React, { useEffect, useRef, useState } from 'react';
import { ViewerImplementation } from '../components/Viewer/ViewerImplementation';
import { GoogleAnalyticsInit, ReportCustomEvent, ReportPageView, UserGoogleAnalyticsInit } from '../GoogleAnalyticsConfig';
import { getStandaloneTour } from '../services/tourServices';
import { mapHotspotValues } from '@vva/pannellum-viewer';
import { useParams } from 'react-router-dom';
import TourNotFound from '../components/TourNotFound/TourNotFound';
import BadRequest from '../components/BadRequest/BadRequest';
import ProtectedScreen from '../components/ProtectedScreen/ProtectedScreen';
import VirtualTourLocked from '../components/VirtualTourLocked/VirtualTourLocked';
import LoadingSpinner from '../utils/loading-spinner/loading-spinner';
import { EnumCategory, EnumEvent, EnumPagesTitles } from '../GoogleAnalyticsTrackList';
import {
  IBranding,
  ILeadGenerationConfig,
  IPanoramaVersionsList,
  IRoomsName,
  IViewerDataFloorPlan,
  IViewerPannellumData,
  IViewerPannellumFirstPanorama,
  IViewerPannellumFloorPlan,
  IViewerPannellumPanoramas,
  IViewerPannellumPanoramaSound,
  IViewerPannellumProtectedData,
  IViewerPannellumThumbnail,
} from '../components/Viewer/Interfaces';
import { useSearchParams } from 'react-router-dom';
import EmptyTour from '../components/BadRequest/EmptyTour';

type tourInformation = {
  name: string;
  latitude: number;
  longitude: number;
  id: string;
  userId: string;
  isAutoRotateEnable: boolean;
};

const ViewerPage = (): JSX.Element => {
  // URL params
  const { readCode, mode } = useParams<{ readCode: string; mode: string }>();

  // Component ref
  const componentIsMounted = useRef(true);

  // Authentication status
  const [authentication, setAuthentication] = useState(true);

  // Tour components
  const [tour, setTour] = useState<IViewerPannellumPanoramas>();
  const [sounds, setSounds] = useState<IViewerPannellumPanoramaSound[]>([]);
  const [thumbnails, setThumbnails] = useState<IViewerPannellumThumbnail[]>([]);
  const [floorPlan, setFloorPlan] = useState<IViewerPannellumFloorPlan[]>([]);
  const [firstPanorama, setFirstPanorama] = useState<IViewerPannellumFirstPanorama>();
  const [tourInformation, setTourInformation] = useState<tourInformation>();
  const [roomNames, setRoomNames] = useState<IRoomsName[]>([]);
  const [leadGeneration, setLeadGeneration] = useState<ILeadGenerationConfig>();
  const [floorPlanData, setFloorPlanData] = useState<IViewerDataFloorPlan[]>([]);
  const [panoramaVersions, setPanoramaVersions] = useState<IPanoramaVersionsList>();

  // Rating component
  const [isRating, setIsRating] = useState(false);
  const [ratingToken, setRatingToken] = useState('');

  // Error states
  const [isBadRequest, setIsBadRequest] = useState(false);
  const [NotFound, setNotFound] = useState(false);
  const [isProtected, setIsProtected] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  // Property Name
  const [propertyName, setPropertyName] = useState('');

  // Service response
  const [response, setResponse] = useState<IViewerPannellumData | IViewerPannellumProtectedData>();

  const [loading, setLoading] = useState<boolean>(true);

  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();

  const [liveSharingOption, setLiveSharingOption] = useState(false);
  const [liveSharingName, setLiveSharingName] = useState<string>();
  const [liveSharingTourOwner, setLiveSharingTourOwner] = useState<string>();
  const [brandingOptions, setBrandingOptions] = useState<IBranding>();
  const [panoramaHighRes, setPanoramaHighRes] = useState<boolean>(false);
  const [panoramaHighResAvailable, setPanoramaHighResAvailable] = useState<boolean>(false);
  /**
   * Google analytics
   */
  useEffect(() => {
    // componentDidMount
    //Google report page
    ReportPageView(EnumPagesTitles.ViewerPage);

    // componentWillUnmount
    return () => {
      //
    };
  }, []);

  /**
   * Component did mount
   */
  useEffect(() => {
    // each useEffect can return a cleanup function
    getTourService();
    return () => {
      componentIsMounted.current = false;
    };
    // eslint-disable-next-line
  }, []);

  /**
   * Service response
   */
  useEffect(() => {
    if (response) {
      initializeComponent();
    }
    // eslint-disable-next-line
  }, [response]);

  /**
   * Update tour meta tag description name
   * @param name
   */
  const updateDescriptionTag = (name: string) => {
    if (name !== '') {
      var element = document.querySelector('meta[name="description"]');
      element && element.setAttribute('content', name);
    }
  };

  /**
   * Initialize component from service response
   */
  const initializeComponent = () => {
    // Check if tour is locked first (enabled === false)
    if (response && response.enabled === false) {
      return;
    }
    if (response && response.protected === false) {
      const dataResponse = response as IViewerPannellumData;
      const {
        firstPanorama,
        panoramas,
        sounds,
        thumbnails,
        floorplans,
        dataFloorPlan,
        isAutoRotateEnable,
        latitude,
        longitude,
        name,
        id,
        roomNamesMap,
        leadGeneration,
        userId,
        googleAnalyticsId,
        panoramaVersions,
        branding,
        liveSharingData,
      } = dataResponse;

      if (googleAnalyticsId !== '' && typeof googleAnalyticsId === 'string') {
        // Report event to google analytics
        ReportCustomEvent(EnumCategory.UserOwnAnalytics, EnumEvent.InitiateTourAnalyticsPrivately);

        UserGoogleAnalyticsInit(googleAnalyticsId);
      }

      // Set tour metadata
      updateDescriptionTag(name);

      // Set tour general information
      setTourInformation({ name, latitude, longitude, id, userId, isAutoRotateEnable });

      // Set floor plan with corresponding panorama
      setFloorPlanData(dataFloorPlan);

      // Panorama versions
      setPanoramaVersions(panoramaVersions);

      // Set floor plans
      setFloorPlan(floorplans);

      // Set Tour
      const panoramasMapped = mapHotspotValues(panoramas);
      setTour(panoramasMapped);

      if (sounds && sounds.length > 0) {
        setSounds(sounds);
      }

      // Set Thumbnails
      setThumbnails(thumbnails);

      // Set first panorama
      setFirstPanorama(firstPanorama);

      // Set Room names
      setRoomNames(roomNamesMap);

      // Set Lead generation
      setLeadGeneration(leadGeneration);

      setLiveSharingOption(liveSharingData.liveSharing);
      setLiveSharingName(liveSharingData.name);
      setLiveSharingTourOwner(liveSharingData.tourOwner);

      if (branding) {
        setBrandingOptions(branding);
      }

      // Set Rating permission
      const params = new URLSearchParams(window.location.search);
      const tokenIdFromParams = params.get('token');
      if (mode === 'annotation' && tokenIdFromParams) {
        setIsRating(true);
        setRatingToken(tokenIdFromParams);
      }
    } else {
      setAuthentication(false);
      if (response && response.protected === true) {
        setIsProtected(true);
        setPropertyName(response.name);
      }
    }
  };

  /**
   * Get tour from read code provided
   */
  const getTourService = async () => {
    const response = await getTourData(readCode);
    if (response) {
      setResponse(response);
    }
  };

  /**
   * Get tour data
   * @param readCode
   * @returns tour data
   */
  const getTourData = async (readCode: string = '') => {
    try {
      const response = await getStandaloneTour(readCode);
      if (!response) {
        setIsEmpty(true);
        setAuthentication(false);
        setLoading(false);
      } else if ((response as any) === 'Tour deleted or archived') {
        setIsBadRequest(true);
        setAuthentication(false);
        setLoading(false);
      } else if ((response as IViewerPannellumData).enabled === false) {
        setIsLocked(true);
        setAuthentication(false);
        setLoading(false);
      }
      GoogleAnalyticsInit();
      setLoading(false);
      return response;
    } catch (error: any) {
      if (error.error.statusText === 'Not Found') {
        setNotFound(true);
        setAuthentication(false);
        setLoading(false);
      }
      if ((error as Error).message === 'Bad Request') {
        setIsBadRequest(true);
        setAuthentication(false);
        setLoading(false);
      } else {
        setNotFound(true);
        setAuthentication(false);
        setLoading(false);
      }
    }
  };

  /**
   * Handle service response
   * @returns page destination
   */
  const handleError = () => {
    if (isBadRequest) {
      return <TourNotFound />;
    }
    if (NotFound) {
      return <BadRequest />;
    }
    if (isLocked) {
      return <VirtualTourLocked />;
    }
    if (isProtected) {
      return <ProtectedScreen propertyName={propertyName} />;
    }
    if (isEmpty) {
      return <EmptyTour />;
    }
  };

  return (
    <React.Fragment>
      {loading && <LoadingSpinner />}
      {authentication ? (
        <ViewerImplementation
          panoramaHighRes={panoramaHighRes}
          setPanoramaHighRes={setPanoramaHighRes}
          panoramaHighResAvailable={panoramaHighResAvailable}
          setPanoramaHighResAvailable={setPanoramaHighResAvailable}
          tour={tour!}
          sounds={sounds}
          thumbnails={thumbnails!}
          firstPanorama={firstPanorama!}
          floorPlan={floorPlan}
          floorPlanData={floorPlanData}
          tourInformation={tourInformation}
          roomNames={roomNames}
          leadGeneration={leadGeneration}
          ratingPermission={isRating}
          ratingToken={ratingToken}
          panoramaVersions={panoramaVersions}
          liveSharingOption={liveSharingOption}
          liveSharingName={liveSharingName}
          liveSharingTourOwner={liveSharingTourOwner}
          brandingOptions={brandingOptions}
        />
      ) : (
        handleError()
      )}
    </React.Fragment>
  );
};
export default ViewerPage;
